import { useCallback, useMemo, useState } from "react";
import { LeadScoringConflict } from "@signalist/shared-types";

import {
	useCheckCriteriaConflictsMutation,
	useCreateLeadScoringLogicMutation,
	useDeleteLeadScoringLogicMutation,
	useUpdateScoreCriteriaMutation,
} from "@/pages/Private/redux/score/score.api";

import { ScoreCriteriaType } from "../../schema/score.schema";
import { isTempId, tempId } from "../../shell/configDraft";

import * as ops from "./criteriaDraft";
import { saveErrorText } from "../../shell/saveErrorText";

/**
 * The unsaved lead scorings of the Lead Scoring page (PREC-4055): a working
 * copy of every scoring, the ones to delete, and one save path — create the
 * new ones, delete, then patch what changed. Cells are the matrix's business
 * (`useConfigDraft`); this hook only hands it the ids the API assigned.
 */

export interface ScoringConflicts {
	scoringId: number;
	conflicts: LeadScoringConflict[];
}

export interface ConflictCheckOutcome {
	status: "complete" | "partial";
	scorings: ScoringConflicts[];
}

export interface CriteriaDraftApi {
	scorings: ScoreCriteriaType[];
	dirty: boolean;
	saving: boolean;
	error: string | null;
	update: (id: number, change: (scoring: ScoreCriteriaType) => ScoreCriteriaType) => void;
	add: (name: string) => number;
	duplicate: (sourceId: number, name: string) => number;
	remove: (id: number) => void;
	/** Whether a grade the conflict check reads changed since the last save. */
	needsConflictCheck: () => boolean;
	checkConflicts: () => Promise<ConflictCheckOutcome>;
	/** Temporary id → id the API assigned; null when the save failed. `saving` stays on until `settle`. */
	save: () => Promise<Map<number, number> | null>;
	/** Drops the working copy once the server copy is back in the slice. */
	settle: () => void;
	discard: () => void;
}

export const useCriteriaDraft = (
	criteria: ScoreCriteriaType[],
	texts: { saveError: string }
): CriteriaDraftApi => {
	const [createScoring] = useCreateLeadScoringLogicMutation();
	const [updateScorings] = useUpdateScoreCriteriaMutation();
	const [deleteScoring] = useDeleteLeadScoringLogicMutation();
	const [checkScoring] = useCheckCriteriaConflictsMutation();

	// Null while the page has no edits: the server copy is shown as it is.
	const [draft, setDraft] = useState<ScoreCriteriaType[] | null>(null);
	const [pendingDeletes, setPendingDeletes] = useState<number[]>([]);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const scorings = draft ?? criteria;
	const serverCopy = useCallback(
		(id: number) => criteria.find((scoring) => scoring.id === id),
		[criteria]
	);

	const dirty = useMemo(
		() =>
			pendingDeletes.length > 0 ||
			(draft !== null &&
				draft.some(
					(scoring) => isTempId(scoring.id) || ops.isScoringChanged(serverCopy(scoring.id), scoring)
				)),
		[draft, pendingDeletes, serverCopy]
	);

	const edit = useCallback(
		(change: (current: ScoreCriteriaType[]) => ScoreCriteriaType[]) =>
			setDraft((current) => change(current ?? criteria)),
		[criteria]
	);

	const update = useCallback(
		(id: number, change: (scoring: ScoreCriteriaType) => ScoreCriteriaType) =>
			edit((current) => current.map((scoring) => (scoring.id === id ? change(scoring) : scoring))),
		[edit]
	);

	// The id is drawn before the state update so the caller can select the row right away.
	const add = useCallback(
		(name: string) => {
			const id = tempId();

			edit((current) => [...current, ops.newScoring(name, id)]);

			return id;
		},
		[edit]
	);

	const duplicate = useCallback(
		(sourceId: number, name: string) => {
			const id = tempId();

			edit((current) => {
				const source = current.find((scoring) => scoring.id === sourceId);

				return source ? [...current, ops.duplicateScoring(source, name, id)] : current;
			});

			return id;
		},
		[edit]
	);

	const remove = useCallback(
		(id: number) => {
			if (!isTempId(id)) {
				setPendingDeletes((current) => [...current, id]);
			}

			edit((current) => current.filter((scoring) => scoring.id !== id));
		},
		[edit]
	);

	const changedForConflicts = useCallback(
		() =>
			scorings.filter((scoring) => ops.conflictCriteriaChanged(serverCopy(scoring.id), scoring)),
		[scorings, serverCopy]
	);

	const needsConflictCheck = useCallback(
		() => changedForConflicts().length > 0,
		[changedForConflicts]
	);

	const checkConflicts = useCallback(async (): Promise<ConflictCheckOutcome> => {
		const outcome: ConflictCheckOutcome = { status: "complete", scorings: [] };

		for (const scoring of changedForConflicts()) {
			const result = await checkScoring({ criteria: scoring.criteria }).unwrap();

			if (result.status === "partial") {
				outcome.status = "partial";
			}

			if (result.conflicts.length > 0) {
				outcome.scorings.push({ scoringId: scoring.id, conflicts: result.conflicts });
			}
		}

		return outcome;
	}, [changedForConflicts, checkScoring]);

	const save = useCallback(async (): Promise<Map<number, number> | null> => {
		setSaving(true);
		setError(null);

		const idMap = new Map<number, number>();

		try {
			const known = new Set(criteria.map((scoring) => scoring.id));

			for (const scoring of scorings.filter((row) => isTempId(row.id))) {
				const created = await createScoring({
					name: scoring.name,
					criteria: scoring.criteria,
					scoreCriteriaCompaniesPersonas: [],
				}).unwrap();
				const fresh = created.allCriteria?.find((row) => !known.has(row.id));

				if (!fresh) {
					throw new Error("The created lead scoring was not returned");
				}

				known.add(fresh.id);
				idMap.set(scoring.id, fresh.id);
				// The create endpoint takes name and grades only; the rest lands with a patch.
				await updateScorings({
					allCriteria: [{ ...scoring, id: fresh.id, scoreCriteriaCompaniesPersonas: [] }],
				}).unwrap();
			}

			for (const id of pendingDeletes) {
				await deleteScoring(id).unwrap();
			}

			const changed = scorings.filter(
				(scoring) => !isTempId(scoring.id) && ops.isScoringChanged(serverCopy(scoring.id), scoring)
			);

			if (changed.length > 0) {
				await updateScorings({ allCriteria: changed }).unwrap();
			}

			return idMap;
		} catch (error) {
			setError(saveErrorText(error, texts.saveError));
			setSaving(false);

			return null;
		}
	}, [
		criteria,
		scorings,
		pendingDeletes,
		serverCopy,
		createScoring,
		updateScorings,
		deleteScoring,
		texts,
	]);

	const settle = useCallback(() => {
		setDraft(null);
		setPendingDeletes([]);
		setSaving(false);
	}, []);

	const discard = useCallback(() => {
		setDraft(null);
		setPendingDeletes([]);
		setError(null);
	}, []);

	return {
		scorings,
		dirty,
		saving,
		error,
		update,
		add,
		duplicate,
		remove,
		needsConflictCheck,
		checkConflicts,
		save,
		settle,
		discard,
	};
};
